import { useEffect, useRef } from 'react';
import type { NotificationMarkViewedBody, NotificationType } from '@magimp/shared';
import { setActiveNotificationTarget } from '../activeContext.js';
import type { MarkViewedByTargetVars } from './useMarkNotificationViewedByTarget.js';
import { useMarkNotificationViewedByTarget } from './useMarkNotificationViewedByTarget.js';
import { useNotificationsByEntity, entityKey } from './useNotificationsByEntity.js';

// WHY (#1359, #1380): derive from the by-target schema, which accepts the
//   LEAD / lead_callback pair exercised by useAutoMarkLeadCallbackViewed.
type MarkViewedNotificationType = MarkViewedByTargetVars['notificationType'];

const CARRIERS_TYPES: readonly MarkViewedNotificationType[] = ['rate', 'rejection', 'insurance'];

// WHY (#2140): `archive` carries no notification aggregates (like `files`);
//   it falls through to the `responsibleStatus`-only default below.
export type DrawerTabKind =
  | 'info'
  | 'items'
  | 'boxes'
  | 'carriers'
  | 'chat'
  | 'audit'
  | 'files'
  | 'archive';

function tabsViewingFor(tab: DrawerTabKind): ReadonlySet<NotificationType> {
  // INVARIANT (#1072 Codex F-001): every drawer mount keeps
  //   `responsibleStatus` active so a fresh realtime event for the open
  //   request gets cleared by the active-context listener regardless of
  //   which tab is focused.
  const set = new Set<NotificationType>(['responsibleStatus']);
  if (tab === 'carriers') for (const t of CARRIERS_TYPES) set.add(t);
  else if (tab === 'chat') set.add('chat');
  return set;
}

/**
 * @aiPurpose drawer mount + tab-change auto mark-viewed (#1072). Two
 *   responsibilities:
 *   1. publish the active (entity, tabs) so the realtime handler can
 *      optimistically clear an incoming aggregate before the row dot
 *      flashes;
 *   2. fire `mark-viewed-by-target` on activation. Fires eagerly even
 *      when no aggregate is cached yet so the backend records a viewed
 *      marker for the race-safe UPSERT in #1071 — the absent-aggregate
 *      race was the Codex F-001 blocker on #1072.
 * @aiInvariant per-(entityType, entityId, type) "activation" guard
 *   prevents tight repeats on every render. Realtime events that
 *   surface a new aggregate id re-arm via the per-type cursor.
 */
// WHY (#1359, #1380): entityType stays derived from the by-target schema,
//   which accepts the LEAD / lead_callback pair exercised by
//   useAutoMarkLeadCallbackViewed. Current China/Russia drawer callers pass
//   request-domain entity types.
export function useAutoMarkNotificationsViewed(args: {
  entityType: Extract<NotificationMarkViewedBody, { by: 'target' }>['entityType'];
  entityId: string;
  currentTab: DrawerTabKind;
}) {
  const { entityType, entityId, currentTab } = args;
  const { mutate } = useMarkNotificationViewedByTarget();
  const { byEntity } = useNotificationsByEntity();

  // Per-(entity, type) activation guard. Once we've fired for a
  // (target, type) we won't fire again until the cursor below shifts.
  const activatedRef = useRef(new Set<string>());
  // Per-(entity, type) cursor on the newest aggregate id, so a fresh
  // realtime event re-arms the activation guard.
  const seenCursorRef = useRef(new Map<string, string>());

  // 1. Publish active target for the realtime handler.
  useEffect(() => {
    setActiveNotificationTarget({
      entityType,
      entityId,
      tabsViewing: tabsViewingFor(currentTab),
    });
    return () => {
      setActiveNotificationTarget(null);
    };
  }, [entityType, entityId, currentTab]);

  // 2. Eager fire on (entity, tab) activation; re-fire when a new
  //    aggregate id appears for that (target, type).
  useEffect(() => {
    const summary = byEntity.get(entityKey(entityType, entityId));

    const fireFor = (type: MarkViewedNotificationType) => {
      const cursorKey = `${entityType}:${entityId}:${type}`;
      const matches = summary?.unreadAggregates.filter((r) => r.notificationType === type) ?? [];
      const newest =
        matches.length === 0
          ? null
          : matches.reduce((a, b) =>
              Date.parse(a.lastEventAt) >= Date.parse(b.lastEventAt) ? a : b,
            );

      const prevCursor = seenCursorRef.current.get(cursorKey);
      const cursorAdvanced = newest !== null && prevCursor !== newest.id;
      const firstActivation = !activatedRef.current.has(cursorKey);

      if (!cursorAdvanced && !firstActivation) return;

      if (newest !== null) seenCursorRef.current.set(cursorKey, newest.id);
      activatedRef.current.add(cursorKey);
      mutate({ entityType, entityId, notificationType: type });
    };

    fireFor('responsibleStatus');
    if (currentTab === 'carriers') {
      for (const t of CARRIERS_TYPES) fireFor(t);
    } else if (currentTab === 'chat') {
      fireFor('chat');
    }
  }, [byEntity, entityType, entityId, currentTab, mutate]);

  // 3. Reset the activation guard when the target changes — a different
  //    drawer must re-fire on its own first mount.
  useEffect(() => {
    return () => {
      activatedRef.current.clear();
      seenCursorRef.current.clear();
    };
  }, [entityType, entityId]);
}
